import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import api from "@/lib/api";
import {
  ArrowLeft, ChatCircleText, WhatsappLogo, PhoneCall, GlobeHemisphereEast, Users, Waveform,
  VideoCamera, IdentificationCard, Printer, AddressBook, Wallet, PaperPlaneTilt, Receipt, SimCard,
  Gift, MagnifyingGlass, Car, ForkKnife, Truck, ShieldCheck, Storefront, SquaresFour, Briefcase, FilmSlate,
  CalendarCheck, ArrowRight,
} from "@phosphor-icons/react";

const C = { bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00", text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)" };
const HEAD = { fontFamily: "'Outfit', sans-serif" };

// Intent deep-links tapped from SMS / WhatsApp: /services?i=<slug>
// match  -> the exact card label to scroll to + highlight
// target -> the real in-app checkout page the buyer lands on after auth
const INTENT = {
  esim:      { match: "eSIM",                       target: "/app/esim?c=US",       label: "your travel eSIM" },
  business:  { match: "Cloud PBX",                  target: "/app/business",        label: "your business line + SI receptionist", type: "business" },
  numbers:   { match: "Get a Global Number",        target: "/app/numbers?open=1",  label: "your local number + E911" },
  tollfree:  { match: "Get a Global Number",        target: "/app/numbers?open=1",  label: "your toll-free number" },
  meet:      { match: "Meetings",                   target: "/app/meet",     label: "HD meetings" },
  calls:     { match: "Top-Ups & Digital Services", target: "/app/topup",    label: "airtime & top-ups" },
  broadcast: { match: "Media Studio",               target: "/app/broadcast",       label: "voice, SMS & email broadcasts" },
  fax:       { match: "Cloud Fax",                  target: "/app/fax",             label: "Cloud Fax / eFax" },
  aisales:   { match: "",                           target: "/app/ai-sales/plans",  label: "iYeneh SI Sales", type: "business" },
  appointments: { match: "SI Appointment Scheduling", target: "/app/appointments", label: "SI Appointment Scheduling for your business", type: "business" },
  ask:       { match: "",                           target: "/app/ai-sales",        label: "Ask iYeneh" },
};

const GROUPS = [
  { title: "Communications", color: C.green, items: [
    { icon: ChatCircleText, label: "Messages", sub: "Free in-app chat, voice notes & SMS" },
    { icon: WhatsappLogo, label: "WhatsApp Business", sub: "Message customers on WhatsApp" },
    { icon: PhoneCall, label: "Call Settings", sub: "Forwarding, voicemail & caller ID" },
    { icon: GlobeHemisphereEast, label: "Get a Global Number", sub: "Local numbers around the world" },
    { icon: Users, label: "Cloud PBX", sub: "Extensions, IVR & SI receptionist" },
    { icon: CalendarCheck, label: "SI Appointment Scheduling", sub: "Let patients & clients book by phone 24/7" },
    { icon: Waveform, label: "Recordings & SI Transcripts", sub: "Secure vault & searchable transcripts" },
    { icon: VideoCamera, label: "Meetings", sub: "Group video & recordings" },
    { icon: IdentificationCard, label: "SIP Trunking", sub: "Channels & routing", soon: true },
    { icon: Printer, label: "Cloud Fax", sub: "Send & receive fax as PDF" },
    { icon: AddressBook, label: "Contacts", sub: "Centralised address book", soon: true },
  ] },
  { title: "Money & Mobile", color: C.orange, items: [
    { icon: Wallet, label: "Wallet", sub: "One balance, immutable ledger" },
    { icon: PaperPlaneTilt, label: "Send Wallet Credit", sub: "Move credit to another member" },
    { icon: Receipt, label: "Billing & Transactions", sub: "Receipts & full history" },
    { icon: GlobeHemisphereEast, label: "Top-Ups & Digital Services", sub: "Airtime, data, bills & gift cards" },
    { icon: SimCard, label: "eSIM", sub: "Travel data in 190+ destinations" },
    { icon: Gift, label: "Refer & Earn", sub: "Invite friends — you both earn" },
    { icon: MagnifyingGlass, label: "Number Lookup", sub: "Carrier, line type & caller ID" },
  ] },
  { title: "Ride, Food & Fleet", color: C.green, items: [
    { icon: Car, label: "iYeneh Ride", sub: "Book a ride or drive to earn" },
    { icon: ForkKnife, label: "iYeneh Eat", sub: "Order food, sell or deliver" },
    { icon: Truck, label: "Fleet", sub: "Track vehicles, fuel & cost per mile" },
    { icon: ShieldCheck, label: "Driver & Fleet Verification", sub: "Inspector approvals & compliance" },
  ] },
  { title: "Business & Earning", color: C.orange, items: [
    { icon: Storefront, label: "Reseller Portal", sub: "Sign up members & earn commission" },
    { icon: SquaresFour, label: "Products & Services", sub: "Browse everything iYeneh offers" },
    { icon: WhatsappLogo, label: "WhatsApp Business", sub: "Serve customers at scale" },
    { icon: Users, label: "Cloud PBX", sub: "Business communications suite" },
    { icon: Briefcase, label: "Freelance Marketplace", sub: "Hire talent or earn, paid via escrow" },
  ] },
  { title: "Media & Productivity", color: C.green, items: [
    { icon: FilmSlate, label: "Media Studio", sub: "Audio, video & broadcasts" },
    { icon: AddressBook, label: "Contacts", sub: "Groups, tags & history", soon: true },
    { icon: Printer, label: "Cloud Fax", sub: "Fax PDFs to anyone" },
  ] },
];

export default function Services() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const slug = (params.get("i") || "").toLowerCase().trim();
  const intent = INTENT[slug] || null;
  const loggedIn = !!(typeof window !== "undefined" && localStorage.getItem("nexus_token"));
  const [highlight, setHighlight] = useState(!!intent);
  const cardRef = useRef(null);
  // First card matching the intent (labels can repeat across groups) — only this one highlights/scrolls.
  const firstMatch = React.useMemo(() => {
    if (!intent) return null;
    for (let gi = 0; gi < GROUPS.length; gi++) {
      const ii = GROUPS[gi].items.findIndex((it) => it.label === intent.match);
      if (ii >= 0) return `${gi}-${ii}`;
    }
    return null;
  }, [intent]);

  // Where the tapped-through buyer should end up: the real checkout after auth.
  const registerUrl = intent
    ? `/register?next=${encodeURIComponent(intent.target)}${intent.type ? `&type=${intent.type}` : ""}`
    : "/register";
  const continueTo = () => navigate(loggedIn ? intent.target : registerUrl);

  useEffect(() => {
    if (!intent) return;
    // Logged-in buyers skip the marketing page entirely — straight to checkout.
    if (loggedIn) { navigate(intent.target, { replace: true }); return; }
    // Anonymous: scroll to the matching card and pulse it so it can't be missed.
    const t = setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
    const off = setTimeout(() => setHighlight(false), 4600);
    return () => { clearTimeout(t); clearTimeout(off); };
  }, [intent, loggedIn, navigate]);

  useEffect(() => {
    if (slug && intent) api.post("/track/tap", { slug }).catch(() => {});
  }, [slug, intent]);

  const showBanner = intent && !loggedIn;

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Manrope',sans-serif" }} className="min-h-screen">
      <header className="sticky top-0 z-40 border-b bg-[#0B1220]/85 backdrop-blur-xl" style={{ borderColor: C.border }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="services-back" className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white"><ArrowLeft size={18} /> Back</Link>
          <Link to={registerUrl} data-testid="services-create-account" className="rounded-full px-4 py-2.5 text-sm font-bold text-white" style={{ background: C.orange }}>Create Free Account</Link>
        </div>
      </header>

      {showBanner && (
        <div className="sticky top-[57px] z-30 border-b backdrop-blur-xl" style={{ borderColor: C.border, background: "rgba(255,122,0,0.12)" }} data-testid="intent-banner">
          <div className="mx-auto flex max-w-6xl flex-col items-start gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="text-sm font-semibold">You're one step from <span style={{ color: C.orange }}>{intent.label}</span> — create your free account to check out.</p>
            <button onClick={continueTo} data-testid="intent-continue" className="inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>
              Continue <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="services-page">
        <h1 style={HEAD} className="text-3xl font-black tracking-tight sm:text-4xl">All Services</h1>
        <p className="mt-2 max-w-2xl text-base" style={{ color: C.sub }}>Everything you need to connect, communicate, move, pay, earn and do business — from one account and one wallet.</p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="vertical-links">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.sub }}>SI Receptionist by industry:</span>
          {[["dentists", "Dentists"], ["hvac", "HVAC"], ["salons", "Salons"]].map(([slug, lbl]) => (
            <Link key={slug} to={`/receptionist/${slug}`} data-testid={`vertical-link-${slug}`}
              className="rounded-full border px-3.5 py-1.5 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
              style={{ borderColor: C.border, background: "#ffffff08" }}>{lbl}</Link>
          ))}
        </div>

        <div className="mt-9 space-y-10">
          {GROUPS.map((g, gi) => (
            <section key={g.title} data-testid={`group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-3">
                <span className="h-4 w-1.5 rounded-full" style={{ background: g.color }} />
                <h2 style={HEAD} className="text-xl font-extrabold">{g.title}</h2>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {g.items.map((it, ii) => {
                  const isTarget = firstMatch === `${gi}-${ii}`;
                  return (
                    <Link key={it.label} to={isTarget ? registerUrl : "/register"}
                      ref={isTarget ? cardRef : undefined}
                      data-testid={`service-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}-g${gi}`}
                      className={`group flex items-center gap-4 rounded-2xl border bg-[#111a2e] p-4 transition-all hover:-translate-y-0.5 hover:border-white/25 ${isTarget && highlight ? "animate-pulse" : ""}`}
                      style={{ borderColor: isTarget && highlight ? C.orange : C.border, boxShadow: isTarget && highlight ? "0 0 0 2px #FF7A00, 0 10px 40px -10px rgba(255,122,0,0.6)" : "none" }}>
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "#ffffff08", color: g.color }}><it.icon size={22} weight="duotone" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{it.label}</p>
                          {it.soon && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ background: "#ffffff12", color: C.sub }}>Soon</span>}
                          {isTarget && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white" style={{ background: C.orange }}>Tap to buy</span>}
                        </div>
                        <p className="truncate text-xs" style={{ color: C.sub }}>{it.sub}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 rounded-3xl border p-8 text-center" style={{ background: C.surface, borderColor: C.border }}>
          <h3 style={HEAD} className="text-2xl font-extrabold">Get started with free calling credit</h3>
          <p className="max-w-md text-sm" style={{ color: C.sub }}>Create your free account and unlock every iYeneh service from one wallet.</p>
          <Link to={registerUrl} data-testid="services-cta-bottom" className="mt-2 rounded-full px-8 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>Create Free Account</Link>
        </div>
      </div>
      <WhatsAppFab />
    </div>
  );
}
